import React, { useState } from 'react';
import { OverFullWindow } from 'components/UI';
import Button from 'components/UI/V3/Button/Button';
import { PriceField } from 'components/UI/V3/Forms/PriceField';
import styles from './WalletAdjustmentModal.module.scss';

const EXCEEDS_BALANCE_ERROR = 'Amount exceeds the current wallet balance.';

const CONFIG = {
    add: {
        headerTitle: 'Adding Balance',
        title: 'Add Balance',
        description: 'Add an amount to apply as a balance.'
    },
    reduce: {
        headerTitle: 'Reduce Balance',
        title: 'Reduce Balance',
        description: "Reduce the client's account balance by"
    }
} as const;

export interface WalletAdjustmentModalProps {
    mode: 'add' | 'reduce';
    availableBalance?: number;
    onClose: () => void;
    onSave: (amount: number) => void;
    isSaving?: boolean;
}

const WalletAdjustmentModal: React.FC<WalletAdjustmentModalProps> = ({
    mode,
    availableBalance = 0,
    onClose,
    onSave,
    isSaving = false
}) => {
    const [amount, setAmount] = useState<number | undefined>(undefined);
    const [error, setError] = useState<string | undefined>();

    const { headerTitle, title, description } = CONFIG[mode];
    const isValid = !!amount && amount > 0;

    const handleSave = () => {
        if (!isValid) return;
        if (mode === 'reduce' && amount > availableBalance) {
            setError(EXCEEDS_BALANCE_ERROR);
            return;
        }
        onSave(amount);
    };

    return (
        <OverFullWindow
            title={headerTitle}
            onClose={onClose}
            showMobileLogo
            centered
        >
            <div className={styles.wrapper}>
                <div className={styles.contentArea}>
                    <div className={styles.content}>
                        <p className={styles.title}>{title}</p>
                        <p className={styles.description}>{description}</p>

                        <div className={styles.fieldWrapper}>
                            <PriceField
                                value={amount}
                                placeholder="$0.00"
                                autoFocus
                                scale={2}
                                precision={8}
                                errors={[]}
                                onChange={val => {
                                    setAmount(val);
                                    setError(undefined);
                                }}
                            />
                            {error && <p className={styles.errorMessage}>{error}</p>}
                        </div>
                    </div>
                </div>

                <div className={styles.footer}>
                    <Button
                        label={isSaving ? 'Saving...' : 'Save'}
                        variant="primary"
                        size="medium"
                        isLoading={isSaving}
                        disabled={!isValid || isSaving}
                        noColorOnDisabled
                        onClick={handleSave}
                    />
                </div>
            </div>
        </OverFullWindow>
    );
};

export default WalletAdjustmentModal;
